import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Webhook, Copy, Trash2, Plus, X, Info, ScrollText, Zap, CheckCircle2, AlertCircle, Sprout } from "lucide-react";
import api, { formatErr } from "../../lib/apiClient";

const SCOPES_ALL = [
  { id: "customers:read",         label: "Read customers" },
  { id: "invoices:read",          label: "Read invoices" },
  { id: "invoices:create_draft",  label: "Create/patch Draft invoices" },
  { id: "invoices:mark_ready",    label: "Mark Draft → Ready" },
  { id: "payments:read",          label: "Read payments" },
];
const EVENTS_ALL = ["invoice.sent", "invoice.paid", "invoice.overdue"];

function Pill({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
    blue:  "bg-blue-50 text-blue-700 border-blue-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${tones[tone]}`}>{children}</span>;
}

function CopyBtn({ value }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      data-testid="copy-secret-btn"
      onClick={async () => { try { await navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); } catch { /* ignore */ } }}
      className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1"
    >
      <Copy size={11} /> {ok ? "Copied" : "Copy"}
    </button>
  );
}

function SecretReveal({ secret, onDone, label = "API Key" }) {
  return (
    <div data-testid="secret-reveal" className="border border-amber-300 bg-amber-50 rounded p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-amber-900 text-sm">Save this {label} now</div>
        <button onClick={onDone} data-testid="secret-dismiss-btn" className="text-amber-900 hover:bg-amber-100 rounded p-1"><X size={14} /></button>
      </div>
      <p className="text-xs text-amber-800">This is the ONLY time we&apos;ll show the full secret. Store it in your assistant&apos;s config now.</p>
      <div className="flex gap-2 items-center">
        <code data-testid="secret-value" className="flex-1 font-mono text-xs bg-white border border-amber-200 rounded px-2 py-2 select-all break-all">{secret}</code>
        <CopyBtn value={secret} />
      </div>
    </div>
  );
}

// ---------- Sandbox seed card ----------
function SandboxSeedCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ customers: 5, invoices_per_customer: 2 });
  const [counter, setCounter] = useState(null);
  const [nextNumInput, setNextNumInput] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const loadCounter = async () => {
    try { const { data } = await api.get("/admin/api-keys/invoice-counter"); setCounter(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadCounter(); }, []);

  const seed = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/admin/api-keys/sandbox/seed", null, { params: form });
      setResult(data);
      await loadCounter();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const wipe = async () => {
    if (!window.confirm("Delete ALL sandbox customers and invoices? Live records are untouched.")) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.delete("/admin/api-keys/sandbox/wipe");
      setResult({ wiped: data.deleted });
      await loadCounter();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const resetCounter = async () => {
    const n = parseInt(nextNumInput);
    if (!n || n < 1) { setErr("Enter the next invoice number as a positive integer"); return; }
    if (!window.confirm(`Reset the live invoice counter so the NEXT real invoice is #${String(n).padStart(5,"0")}?`)) return;
    setResetBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/api-keys/invoice-counter/reset", { next_number: n });
      setResult({ resetTo: data.next_number_will_be, before: data.before });
      setNextNumInput("");
      await loadCounter();
    } catch (e) { setErr(formatErr(e)); }
    finally { setResetBusy(false); }
  };

  return (
    <section data-testid="sandbox-seed-card" className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-50 text-emerald-700 p-2 mt-0.5"><Sprout size={18} /></div>
          <div>
            <h3 className="font-display font-semibold flex items-center gap-2">Sandbox test data</h3>
            <p className="text-xs text-slate-500 mt-1">Sandbox keys (<code>plmbk_test_</code>) can&apos;t create customers. Seed a few fake customers + Draft invoices here for your integration tests. Live records are never touched, and sandbox invoices use their own <code>SB-#####</code> numbering that can never bump your real invoice sequence.</p>
          </div>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[11px] font-medium text-slate-700 uppercase tracking-wider">Customers</label>
          <input type="number" min={1} max={20} value={form.customers} data-testid="seed-customers-input"
                 onChange={e => setForm({ ...form, customers: Math.max(1, Math.min(20, parseInt(e.target.value) || 1)) })}
                 className="mt-1 w-24 px-3 py-2 text-sm border border-slate-300 rounded tabular" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-700 uppercase tracking-wider">Invoices / customer</label>
          <input type="number" min={0} max={5} value={form.invoices_per_customer} data-testid="seed-invoices-input"
                 onChange={e => setForm({ ...form, invoices_per_customer: Math.max(0, Math.min(5, parseInt(e.target.value) || 0)) })}
                 className="mt-1 w-24 px-3 py-2 text-sm border border-slate-300 rounded tabular" />
        </div>
        <button onClick={seed} disabled={busy} data-testid="seed-sandbox-btn"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded font-medium flex items-center gap-1.5 disabled:opacity-50">
          <Sprout size={13}/> {busy ? "Seeding…" : "Seed sandbox data"}
        </button>
        <button onClick={wipe} disabled={busy} data-testid="wipe-sandbox-btn"
                className="ml-auto px-3 py-2 text-red-700 hover:bg-red-50 border border-red-200 text-sm rounded flex items-center gap-1.5 disabled:opacity-50">
          <Trash2 size={13}/> Wipe sandbox
        </button>
      </div>

      {result && (
        <div data-testid="seed-result" className="text-sm bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-900 flex items-center gap-2">
          <CheckCircle2 size={14}/>
          {result.wiped
            ? <>Wiped {result.wiped.customers} sandbox customer(s) and {result.wiped.invoices} invoice(s).</>
            : result.resetTo
              ? <>Live counter reset — next real invoice will be <strong>#{result.resetTo}</strong>. Previous counter was at {result.before.counter_seq}.</>
              : <>Created {result.created.customers} sandbox customer(s) and {result.created.invoices} draft invoice(s). Sandbox now has {result.totals.sandbox_customers} customer(s) / {result.totals.sandbox_invoices} invoice(s).</>}
        </div>
      )}

      {/* Live counter status + reset — appears if the counter has drifted above the live max */}
      {counter && (
        <div data-testid="counter-status" className="mt-2 border-t border-slate-100 pt-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-600 space-y-0.5">
              <div>Live invoice counter: <strong className="text-slate-900">next = #{counter.next_number_would_be}</strong></div>
              <div className="text-[11px] text-slate-500">Highest existing live invoice: #{String(counter.max_live_number).padStart(5,"0")} • Internal seq: {counter.counter_seq} • Start floor: {counter.settings_floor || "(unset)"}</div>
            </div>
            {counter.counter_seq > counter.max_live_number + 1 && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Counter is ahead of your highest live invoice — you can reset below.
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 flex-wrap mt-3">
            <div>
              <label className="text-[11px] font-medium text-slate-700 uppercase tracking-wider">Force next invoice to be #</label>
              <input type="number" value={nextNumInput} data-testid="reset-counter-input"
                     onChange={e => setNextNumInput(e.target.value)}
                     placeholder="e.g. 2040"
                     className="mt-1 w-40 px-3 py-2 text-sm border border-slate-300 rounded tabular" />
            </div>
            <button onClick={resetCounter} disabled={resetBusy || !nextNumInput} data-testid="reset-counter-btn"
                    className="px-3 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded disabled:opacity-50">
              {resetBusy ? "Resetting…" : "Reset counter"}
            </button>
            <p className="text-[11px] text-slate-500 basis-full">Must be strictly higher than your current highest live invoice (#{String(counter.max_live_number).padStart(5,"0")}). Sandbox rows are ignored.</p>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- One-click onboarding card ----------
function OnboardCard({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", webhook_url: "", sandbox: true });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const body = { name: form.name.trim(), sandbox: form.sandbox };
      if (form.webhook_url.trim()) body.webhook_url = form.webhook_url.trim();
      const { data } = await api.post("/admin/onboard", body);
      setResult(data);
      onDone && onDone();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reset = () => { setOpen(false); setResult(null); setForm({ name: "", webhook_url: "", sandbox: true }); setErr(""); };

  return (
    <section data-testid="onboard-card" className="bg-gradient-to-br from-[#0F4C81] to-[#0A365C] text-white border border-[#0A365C] rounded-md p-6 space-y-3">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-white/10 p-2 mt-0.5"><Zap size={18} /></div>
          <div>
            <h3 className="font-display font-semibold text-lg">Quick setup for a new assistant</h3>
            <p className="text-sm text-white/80 mt-0.5">One click gives your assistant an API key, a signed webhook, and a copy-paste <code className="bg-white/10 px-1 py-0.5 rounded text-xs">.env</code> snippet. Takes ~30 seconds.</p>
          </div>
        </div>
        {!open && !result && (
          <button data-testid="start-onboard-btn" onClick={() => setOpen(true)} className="px-4 py-2 bg-white text-[#0F4C81] text-sm font-semibold rounded shadow hover:bg-slate-100">
            Start setup
          </button>
        )}
      </div>

      {open && !result && (
        <form onSubmit={submit} data-testid="onboard-form" className="bg-white text-slate-900 rounded p-4 space-y-3">
          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Assistant name</label>
            <input required data-testid="onboard-name-input" value={form.name}
                   onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Home Assistant, N8N, Zapier"
                   className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Webhook URL <span className="text-slate-400 normal-case">(optional — leave blank for polling)</span></label>
            <input type="url" data-testid="onboard-webhook-input" value={form.webhook_url}
                   onChange={e => setForm({ ...form, webhook_url: e.target.value })}
                   placeholder="https://your-assistant.local/webhooks/plumbing"
                   className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded" />
            <p className="text-[11px] text-slate-500 mt-1">If provided, we&apos;ll create a signed subscription and send one test delivery immediately.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="onboard-sandbox-checkbox" checked={form.sandbox} onChange={e => setForm({ ...form, sandbox: e.target.checked })} />
            <span>Sandbox setup <span className="text-slate-500">— recommended for first-time integration (isolates data).</span></span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={reset} className="px-3 py-1.5 text-xs border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={busy || !form.name} data-testid="onboard-submit-btn"
                    className="px-4 py-1.5 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded disabled:opacity-50 flex items-center gap-1.5">
              {busy ? "Setting up…" : (<><Zap size={13}/> Provision</>)}
            </button>
          </div>
        </form>
      )}

      {result && (
        <div data-testid="onboard-result" className="bg-white text-slate-900 rounded p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
              <CheckCircle2 size={16}/> Assistant &quot;{result.api_key.name}&quot; is provisioned
            </div>
            <button data-testid="onboard-done-btn" onClick={reset} className="text-slate-500 hover:bg-slate-100 rounded p-1"><X size={14}/></button>
          </div>

          {result.test_delivery && (
            <div data-testid="onboard-test-result" className={`text-xs px-3 py-2 rounded border flex items-center gap-2 ${result.test_delivery.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              {result.test_delivery.ok ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
              Test delivery to <code className="text-[11px]">{result.test_delivery.url}</code> →
              <strong className="ml-1">{result.test_delivery.status_code || "no response"}</strong>
              {result.test_delivery.error && <span className="text-[11px]">— {result.test_delivery.error}</span>}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-slate-700 uppercase tracking-wider">Copy this into your assistant&apos;s <code>.env</code></div>
              <CopyBtn value={result.env_snippet} />
            </div>
            <pre data-testid="onboard-env-snippet" className="text-xs font-mono bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto whitespace-pre">{result.env_snippet}</pre>
          </div>

          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            ⚠️ Save these secrets NOW — they are shown only this once. You can always revoke and regenerate below.
          </div>

          <div className="text-sm text-slate-600">
            Next step: point your assistant at <code>{result.docs_url}</code> for the endpoint reference, then poll <code>GET /api/v1/invoices?updated_since=…</code> or handle the signed webhook body.
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- API Keys card ----------
function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", sandbox: false, scopes: SCOPES_ALL.map(s => s.id), rate_limit_per_min: 60 });
  const [newSecret, setNewSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hideRevoked, setHideRevoked] = useState(true);

  const load = async () => { try { const { data } = await api.get("/admin/api-keys"); setKeys(data || []); } catch (e) { setErr(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/api-keys", form);
      setNewSecret({ secret: data.secret, name: data.name });
      setShowCreate(false);
      setForm({ name: "", sandbox: false, scopes: SCOPES_ALL.map(s => s.id), rate_limit_per_min: 60 });
      await load();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const revoke = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? Anything using it will stop working immediately.`)) return;
    try { await api.delete(`/admin/api-keys/${id}`); await load(); } catch (e) { setErr(formatErr(e)); }
  };

  const toggleScope = (id) => setForm(f => ({ ...f, scopes: f.scopes.includes(id) ? f.scopes.filter(s => s !== id) : [...f.scopes, id] }));

  return (
    <section data-testid="api-keys-card" className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div className="border-b border-slate-100 pb-3 mb-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><KeyRound size={16} className="text-[#0F4C81]" /> API Keys</h3>
          <p className="text-xs text-slate-500 mt-1">Bearer tokens for external assistants. Live keys touch production data; sandbox keys touch only <code>sandbox: true</code> records.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 mr-1">
            <input type="checkbox" data-testid="hide-revoked-toggle" checked={hideRevoked} onChange={e => setHideRevoked(e.target.checked)} />
            Hide revoked
          </label>
          <Link to="/api/v1/docs" target="_blank" className="text-xs text-[#0F4C81] hover:underline flex items-center gap-1" data-testid="api-docs-link" onClick={(e) => { e.preventDefault(); window.open("/api/v1/docs", "_blank"); }}>
            <ScrollText size={12} /> API docs
          </Link>
          <button data-testid="new-api-key-btn" onClick={() => setShowCreate(v => !v)} className="px-3 py-1.5 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-xs rounded font-medium flex items-center gap-1.5">
            <Plus size={12}/> New key
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
      {newSecret && <SecretReveal secret={newSecret.secret} onDone={() => setNewSecret(null)} label={`API Key (${newSecret.name})`} />}

      {showCreate && (
        <form onSubmit={create} data-testid="create-api-key-form" className="border border-slate-200 rounded p-4 space-y-3 bg-slate-50">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Name</label>
              <input required data-testid="api-key-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Local Assistant" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Rate limit (req/min)</label>
              <input type="number" min={1} max={600} data-testid="api-key-rate-input" value={form.rate_limit_per_min}
                     onChange={e => setForm({ ...form, rate_limit_per_min: parseInt(e.target.value) || 60 })}
                     className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white tabular" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="api-key-sandbox-checkbox" checked={form.sandbox} onChange={e => setForm({ ...form, sandbox: e.target.checked })} />
            <span>Sandbox key <span className="text-slate-500">— only sees records marked <code>sandbox: true</code>. Test freely without touching real data.</span></span>
          </label>
          <div>
            <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">Scopes</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {SCOPES_ALL.map(s => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`api-key-scope-${s.id}`} checked={form.scopes.includes(s.id)} onChange={() => toggleScope(s.id)} />
                  <span><code className="text-[11px] text-slate-600">{s.id}</code> — {s.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-xs border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={busy || !form.name || form.scopes.length === 0} data-testid="create-api-key-submit"
                    className="px-3 py-1.5 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-xs rounded disabled:opacity-50">
              {busy ? "Creating…" : "Create Key"}
            </button>
          </div>
        </form>
      )}

      {(() => {
        const visible = hideRevoked ? keys.filter(k => k.active) : keys;
        const revokedCount = keys.filter(k => !k.active).length;
        if (keys.length === 0) {
          return <div className="text-sm text-slate-500 italic text-center py-6" data-testid="api-keys-empty">No API keys yet. Create one to let your local assistant read customers and draft invoices.</div>;
        }
        if (visible.length === 0) {
          return (
            <div className="text-sm text-slate-500 italic text-center py-6" data-testid="api-keys-all-revoked">
              All {revokedCount} keys are revoked. <button onClick={() => setHideRevoked(false)} className="text-[#0F4C81] hover:underline not-italic">Show them</button>.
            </div>
          );
        }
        return (
        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-3 sm:px-2">Name / Env</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden sm:table-cell">Scopes</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden md:table-cell">Rate</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden md:table-cell">Last Used</th>
                <th className="text-left py-2 px-3 sm:px-2">Status</th>
                <th className="py-2 px-3 sm:px-2"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(k => (
                <tr key={k.id} data-testid={`api-key-row-${k.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 px-3 sm:px-2">
                    <div className="font-medium text-slate-900">{k.name}</div>
                    <div className="mt-0.5">{k.sandbox ? <Pill tone="amber">sandbox</Pill> : <Pill tone="green">live</Pill>}</div>
                  </td>
                  <td className="py-2 px-3 sm:px-2 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">{(k.scopes || []).map(s => <code key={s} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{s}</code>)}</div>
                  </td>
                  <td className="py-2 px-3 sm:px-2 hidden md:table-cell tabular text-slate-600">{k.rate_limit_per_min}/min</td>
                  <td className="py-2 px-3 sm:px-2 hidden md:table-cell text-slate-600 text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span className="text-slate-400 italic">never</span>}</td>
                  <td className="py-2 px-3 sm:px-2">{k.active ? <Pill tone="green">active</Pill> : <Pill tone="red">revoked</Pill>}</td>
                  <td className="py-2 px-3 sm:px-2 text-right">
                    {k.active && (
                      <button data-testid={`revoke-api-key-${k.id}`} onClick={() => revoke(k.id, k.name)} className="text-red-600 hover:bg-red-50 rounded p-1.5" title="Revoke"><Trash2 size={14}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hideRevoked && revokedCount > 0 && (
            <div className="text-[11px] text-slate-500 text-center py-2" data-testid="revoked-hidden-hint">
              {revokedCount} revoked key{revokedCount === 1 ? "" : "s"} hidden. <button onClick={() => setHideRevoked(false)} className="text-[#0F4C81] hover:underline">Show</button>
            </div>
          )}
        </div>
        );
      })()}
    </section>
  );
}

// ---------- Webhooks card ----------
function WebhooksCard() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ url: "", events: [...EVENTS_ALL], sandbox: false, active: true });
  const [newSecret, setNewSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState({});

  const load = async () => { try { const { data } = await api.get("/admin/webhook-endpoints"); setItems(data || []); } catch (e) { setErr(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/webhook-endpoints", form);
      setNewSecret({ secret: data.secret, url: data.url });
      setShowCreate(false);
      setForm({ url: "", events: [...EVENTS_ALL], sandbox: false, active: true });
      await load();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const del = async (id, url) => {
    if (!window.confirm(`Delete webhook to ${url}?`)) return;
    try { await api.delete(`/admin/webhook-endpoints/${id}`); await load(); } catch (e) { setErr(formatErr(e)); }
  };

  const toggleActive = async (id, active) => {
    try { await api.patch(`/admin/webhook-endpoints/${id}`, { active: !active }); await load(); } catch (e) { setErr(formatErr(e)); }
  };

  const test = async (id) => {
    setTestMsg(m => ({ ...m, [id]: "Sending…" }));
    try {
      const { data } = await api.post(`/admin/webhook-endpoints/${id}/test`);
      setTestMsg(m => ({ ...m, [id]: data.ok ? `✓ ${data.status_code}` : `✗ ${data.status_code || "err"} — ${data.error || ""}` }));
    } catch (e) { setTestMsg(m => ({ ...m, [id]: formatErr(e) })); }
    setTimeout(() => setTestMsg(m => ({ ...m, [id]: null })), 4000);
  };

  const toggleEvent = (ev) => setForm(f => ({ ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev] }));

  return (
    <section data-testid="webhooks-card" className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div className="border-b border-slate-100 pb-3 mb-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Webhook size={16} className="text-[#0F4C81]" /> Webhooks</h3>
          <p className="text-xs text-slate-500 mt-1">Signed HTTPS POSTs on <code>invoice.sent</code>, <code>invoice.paid</code>, <code>invoice.overdue</code>. Body is HMAC-SHA256 signed with your endpoint secret (header <code>X-Plumbing-Signature</code>).</p>
        </div>
        <button data-testid="new-webhook-btn" onClick={() => setShowCreate(v => !v)} className="px-3 py-1.5 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-xs rounded font-medium flex items-center gap-1.5">
          <Plus size={12}/> New endpoint
        </button>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
      {newSecret && <SecretReveal secret={newSecret.secret} onDone={() => setNewSecret(null)} label={`Signing secret (${newSecret.url})`} />}

      {showCreate && (
        <form onSubmit={create} data-testid="create-webhook-form" className="border border-slate-200 rounded p-4 space-y-3 bg-slate-50">
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Endpoint URL</label>
            <input required type="url" data-testid="webhook-url-input" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
                   placeholder="https://your-assistant.example.com/webhooks/plumbing"
                   className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="webhook-sandbox-checkbox" checked={form.sandbox} onChange={e => setForm({ ...form, sandbox: e.target.checked })} />
            <span>Sandbox endpoint <span className="text-slate-500">— only receives events for <code>sandbox: true</code> records.</span></span>
          </label>
          <div>
            <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">Events</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {EVENTS_ALL.map(ev => (
                <label key={ev} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`webhook-event-${ev}`} checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} />
                  <code className="text-[11px]">{ev}</code>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-xs border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={busy || !form.url || form.events.length === 0} data-testid="create-webhook-submit"
                    className="px-3 py-1.5 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-xs rounded disabled:opacity-50">
              {busy ? "Creating…" : "Create Endpoint"}
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 italic text-center py-6" data-testid="webhooks-empty">No webhook endpoints yet. Add one to receive real-time invoice events.</div>
      ) : (
        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-3 sm:px-2">URL / Env</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden sm:table-cell">Events</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden md:table-cell">Last</th>
                <th className="text-left py-2 px-3 sm:px-2">Status</th>
                <th className="py-2 px-3 sm:px-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(w => (
                <tr key={w.id} data-testid={`webhook-row-${w.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 px-3 sm:px-2 max-w-[280px]">
                    <div className="font-medium text-slate-900 truncate">{w.url}</div>
                    <div className="mt-0.5">{w.sandbox ? <Pill tone="amber">sandbox</Pill> : <Pill tone="green">live</Pill>}</div>
                    {testMsg[w.id] && <div className="text-[11px] text-slate-600 mt-1">{testMsg[w.id]}</div>}
                  </td>
                  <td className="py-2 px-3 sm:px-2 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">{(w.events || []).map(ev => <code key={ev} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{ev}</code>)}</div>
                  </td>
                  <td className="py-2 px-3 sm:px-2 hidden md:table-cell text-xs text-slate-600">
                    {w.last_delivery_at ? (
                      <div>
                        <div>{new Date(w.last_delivery_at).toLocaleString()}</div>
                        {w.last_status != null && <Pill tone={w.last_status >= 200 && w.last_status < 300 ? "green" : "red"}>{w.last_status}</Pill>}
                      </div>
                    ) : <span className="text-slate-400 italic">never</span>}
                  </td>
                  <td className="py-2 px-3 sm:px-2">{w.active ? <Pill tone="green">on</Pill> : <Pill tone="slate">off</Pill>}</td>
                  <td className="py-2 px-3 sm:px-2 text-right whitespace-nowrap">
                    <button data-testid={`test-webhook-${w.id}`} onClick={() => test(w.id)} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 mr-1">Test</button>
                    <button data-testid={`toggle-webhook-${w.id}`} onClick={() => toggleActive(w.id, w.active)} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 mr-1">{w.active ? "Disable" : "Enable"}</button>
                    <button data-testid={`delete-webhook-${w.id}`} onClick={() => del(w.id, w.url)} className="text-red-600 hover:bg-red-50 rounded p-1.5" title="Delete"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------- Call log card ----------
function CallLogCard() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => { try { const { data } = await api.get("/admin/api-keys/log", { params: { limit: 100 } }); setRows(data || []); } catch (e) { setErr(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="api-log-card" className="bg-white border border-slate-200 rounded-md p-6 space-y-3">
      <div className="border-b border-slate-100 pb-3 mb-2 flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Info size={16} className="text-[#0F4C81]" /> API Call Log</h3>
          <p className="text-xs text-slate-500 mt-1">Latest 100 requests hitting <code>/api/v1/*</code>.</p>
        </div>
        <button onClick={load} data-testid="refresh-log-btn" className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Refresh</button>
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 italic text-center py-4">No API calls yet.</div>
      ) : (
        <div className="overflow-x-auto -mx-6 sm:mx-0 max-h-[380px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 sticky top-0 bg-white">
              <tr>
                <th className="text-left py-2 px-3 sm:px-2">When</th>
                <th className="text-left py-2 px-3 sm:px-2 hidden sm:table-cell">Key</th>
                <th className="text-left py-2 px-3 sm:px-2">Endpoint</th>
                <th className="text-left py-2 px-3 sm:px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`log-row-${r.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5 px-3 sm:px-2 text-xs text-slate-600 whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
                  <td className="py-1.5 px-3 sm:px-2 hidden sm:table-cell text-xs">{r.key_name} {r.sandbox ? <Pill tone="amber">sb</Pill> : null}</td>
                  <td className="py-1.5 px-3 sm:px-2 text-xs font-mono">{r.endpoint}</td>
                  <td className="py-1.5 px-3 sm:px-2"><Pill tone={r.status >= 200 && r.status < 300 ? "green" : (r.status === 401 || r.status === 403 ? "amber" : "red")}>{r.status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function SettingsApiKeys() {
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <div data-testid="settings-api-keys-page" className="max-w-4xl space-y-6">
      <div>
        <div className="text-xs text-slate-500 mb-1"><Link to="/settings" className="hover:underline">Settings</Link> / API Keys</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">API Keys &amp; Webhooks</h1>
        <p className="text-sm text-slate-500 mt-1">Grant read/write access to external assistants without giving them your login. <a href="/api/v1/docs" target="_blank" rel="noreferrer" className="text-[#0F4C81] hover:underline">See the API docs →</a></p>
      </div>

      <OnboardCard onDone={() => setReloadKey(k => k + 1)} />
      <SandboxSeedCard />
      <ApiKeysCard key={`k-${reloadKey}`} />
      <WebhooksCard key={`w-${reloadKey}`} />
      <CallLogCard key={`l-${reloadKey}`} />
    </div>
  );
}
